import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import { useEffect } from 'react';
import { z } from 'zod';

import { useCRMAudienceReportMetadata } from '@/api/audience';
import { withFieldGroup } from '@/components/Form';
import { FieldDescription, FieldLabel } from '@/components/Form/fields';
import { ReportName } from '@/components/ReportName';
import { Text } from '@/components/typography';
import { getMessageByCode } from '@/error/messages';
import { INTEGRATION_ERROR_CODES } from '@/error/messages/integration-standard-errors';
import { isHubspotReportUrlValid } from '@/helper/validator';
import useDebounce from '@/hooks/useDebounce';

type Props = {
  isCreatingAudience?: boolean;
};

const getHubspotUrlFieldSchema = () =>
  z
    .string()
    .trim()
    .min(1, t`HubSpot Contacts Link is required`)
    .refine(
      (value) => isHubspotReportUrlValid(value),
      getMessageByCode(INTEGRATION_ERROR_CODES.HUBSPOT_INVALID_URL)!,
    );

const ImportCRMAudienceHubspotForm = withFieldGroup({
  defaultValues: {
    hubspot_url: '',
  },
  props: {
    isCreatingAudience: false,
  } as Props,
  render: function Render({ group, isCreatingAudience = false }) {
    const hubspotUrlFieldSchema = getHubspotUrlFieldSchema();
    const reportURL = useStore(group.store, (state) => state.values['hubspot_url']);
    const debouncedReportURL = useDebounce(reportURL, 1000);
    const {
      reportMetadata,
      isLoading: isReportLoading,
      error: reportError,
    } = useCRMAudienceReportMetadata(
      isHubspotReportUrlValid(debouncedReportURL) ? debouncedReportURL : undefined,
    );
    const reportErrorMessage = reportError?.getFirstError?.().message;

    useEffect(() => {
      void group.validateField('hubspot_url', 'change');
    }, [debouncedReportURL, group, reportErrorMessage]);

    return (
      <div className="flex flex-col gap-4">
        <Text variant="body1">
          <Trans>
            <strong>Note: </strong>
            Your HubSpot Company Segment List must include the standard "
            <strong>Company Domain Name</strong>" field as a column.
          </Trans>
        </Text>
        <group.AppField
          name="hubspot_url"
          validators={{
            onDynamic: hubspotUrlFieldSchema,
            onChangeAsync: async ({ value }) => {
              if (reportErrorMessage && value.trim() === debouncedReportURL) {
                return {
                  message: reportErrorMessage,
                };
              }
            },
          }}
        >
          {(field) => (
            <field.Field>
              <FieldLabel htmlFor="hubspot-report-link">
                <Trans>HubSpot Report Link</Trans>
              </FieldLabel>
              <FieldDescription>
                <Trans>
                  The url should be in the format of{' '}
                  <code>
                    https://app.hubspot.com/contacts/&lt;contact-id&gt;/objectLists/&lt;list-id&gt;
                  </code>
                </Trans>
              </FieldDescription>
              <field.TextInput id="hubspot-report-link" disabled={isCreatingAudience} autoFocus />
              {(reportMetadata || isReportLoading) && (
                <ReportName reportName={reportMetadata?.name} isLoading={isReportLoading} />
              )}
              <field.Error />
            </field.Field>
          )}
        </group.AppField>
      </div>
    );
  },
});

export default ImportCRMAudienceHubspotForm;
